import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, Plus, Trash2, User, Droplets, AlertTriangle, Pill, 
  Activity, Scissors, Stethoscope, Building2, CreditCard, Syringe,
  Cpu, Hospital, Save, Loader2, CheckCircle2, Phone, Star, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import useSharedMedicalData from '@/hooks/useSharedMedicalData';
import API from '@/utils/api';
import SyncStatusIndicator from '@/components/SyncStatusIndicator';
import { getSyncStatus } from '@/services/offlineDataService';
import InsuranceCardScanner from '@/components/InsuranceCardScanner';
import EmptyState from '@/components/EmptyState';
import { useDemo } from '@/contexts/DemoContext';

/**
 * MedicalInfoPage - Complete medical information for Emergency Binder
 * 
 * NOW USES SHARED MEDICAL DATA (Single Source of Truth)
 * All changes here are immediately reflected in:
 * - Emergency Summary
 * - Emergency Mode (Medical Alert Screen)
 * - QR Code public view
 * - Doctor Summary
 * - PDF exports
 */

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const SEVERITY_OPTIONS = ['mild', 'moderate', 'severe', 'life-threatening'];

const MedicalInfoPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [profileName, setProfileName] = useState('');
  const { isDemoMode, demoData } = useDemo();
  
  // Use shared medical data hook - single source of truth
  const {
    loading,
    profile,
    updateProfile,
    allergies,
    addAllergy,
    updateAllergy,
    deleteAllergy,
    medications,
    addMedication,
    updateMedication,
    deleteMedication,
    conditions,
    addCondition,
    updateCondition,
    deleteCondition,
    surgeries,
    addSurgery,
    updateSurgery,
    deleteSurgery,
    emergencyContacts,
    addEmergencyContact,
    updateEmergencyContact,
    deleteEmergencyContact,
    doctors,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    insurance,
    addInsurance,
    updateInsurance,
    deleteInsurance,
  } = useSharedMedicalData();

  // Local form state for new items being added
  const [newAllergy, setNewAllergy] = useState(null);
  const [newMedication, setNewMedication] = useState(null);
  const [newCondition, setNewCondition] = useState(null);
  const [newSurgery, setNewSurgery] = useState(null);
  const [newDoctor, setNewDoctor] = useState(null);
  const [newInsurance, setNewInsurance] = useState(null);
  const [newContact, setNewContact] = useState(null);
  const [showInsuranceScanner, setShowInsuranceScanner] = useState(false);
  
  // Saving states
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAllergy, setSavingAllergy] = useState(null);
  const [savingMedication, setSavingMedication] = useState(null);
  const [savingCondition, setSavingCondition] = useState(null);
  const [savingSurgery, setSavingSurgery] = useState(null);
  const [savingDoctor, setSavingDoctor] = useState(null);
  const [savingInsurance, setSavingInsurance] = useState(null);
  const [savingContact, setSavingContact] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // Track pending sync changes
  useEffect(() => {
    const checkPendingChanges = () => {
      const status = getSyncStatus();
      setHasPendingChanges(status.hasPendingChanges);
    };
    
    checkPendingChanges();
    const interval = setInterval(checkPendingChanges, 5000);
    return () => clearInterval(interval);
  }, [getSyncStatus]);

  // Fetch profile name from emergency binder profile
  useEffect(() => {
    const fetchProfileName = async () => {
      // In demo mode, use demo data for the profile name
      if (isDemoMode) {
        const demoUser = demoData?.user;
        setProfileName(demoUser?.name || 'Michael Carter');
        return;
      }
      
      if (profileId) {
        try {
          const response = await API.get(`/emergency-binder/profiles/${profileId}`);
          setProfileName(response.data.name || 'Medical Information');
        } catch (error) {
          console.error('Failed to fetch profile:', error);
        }
      }
    };
    fetchProfileName();
  }, [profileId, isDemoMode, demoData]);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    date_of_birth: '',
    blood_type: '',
    organ_donor: false,
  });

  // Sync profile data when loaded
  useEffect(() => {
    if (profile) {
      setProfileForm({
        full_name: profile.full_name || '',
        date_of_birth: profile.date_of_birth || '',
        blood_type: profile.blood_type || '',
        organ_donor: profile.organ_donor || false,
      });
    }
  }, [profile]);

  // Save profile info
  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      await updateProfile(profileForm);
      toast.success('Profile saved');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // --- ALLERGY HANDLERS ---
  const handleAddAllergy = () => {
    setNewAllergy({ name: '', reaction: '', severity: '' });
  };

  const handleSaveNewAllergy = async () => {
    if (!newAllergy?.name?.trim()) {
      toast.error('Please enter allergy name');
      return;
    }
    try {
      setSavingAllergy('new');
      await addAllergy(newAllergy);
      setNewAllergy(null);
      toast.success('Allergy added');
    } catch (error) {
      toast.error('Failed to add allergy');
    } finally {
      setSavingAllergy(null);
    }
  };

  const handleUpdateAllergy = async (id, field, value) => {
    try {
      setSavingAllergy(id);
      await updateAllergy(id, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingAllergy(null);
    }
  };

  const handleDeleteAllergy = async (id) => {
    try {
      setSavingAllergy(id);
      await deleteAllergy(id);
      toast.success('Allergy removed');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSavingAllergy(null);
    }
  };

  // --- MEDICATION HANDLERS ---
  const handleAddMedication = () => {
    setNewMedication({ name: '', dosage: '', frequency: '', prescribing_doctor: '', prescribed_for: '', is_active: true });
  };

  const handleSaveNewMedication = async () => {
    if (!newMedication?.name?.trim()) {
      toast.error('Please enter medication name');
      return;
    }
    try {
      setSavingMedication('new');
      await addMedication(newMedication);
      setNewMedication(null);
      toast.success('Medication added');
    } catch (error) {
      toast.error('Failed to add medication');
    } finally {
      setSavingMedication(null);
    }
  };

  const handleUpdateMedication = async (id, field, value) => {
    try {
      setSavingMedication(id);
      await updateMedication(id, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingMedication(null);
    }
  };

  const handleDeleteMedication = async (id) => {
    try {
      setSavingMedication(id);
      await deleteMedication(id);
      toast.success('Medication removed');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSavingMedication(null);
    }
  };

  // --- CONDITION HANDLERS ---
  const handleAddCondition = () => {
    setNewCondition({ condition: '', diagnosed_date: '', diagnosing_doctor: '', status: 'active' });
  };

  const handleSaveNewCondition = async () => {
    if (!newCondition?.condition?.trim()) {
      toast.error('Please enter condition name');
      return;
    }
    try {
      setSavingCondition('new');
      await addCondition(newCondition);
      setNewCondition(null);
      toast.success('Condition added');
    } catch (error) {
      toast.error('Failed to add condition');
    } finally {
      setSavingCondition(null);
    }
  };

  const handleUpdateCondition = async (id, field, value) => {
    try {
      setSavingCondition(id);
      await updateCondition(id, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingCondition(null);
    }
  };

  const handleDeleteCondition = async (id) => {
    try {
      setSavingCondition(id);
      await deleteCondition(id);
      toast.success('Condition removed');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSavingCondition(null);
    }
  };

  // --- SURGERY HANDLERS ---
  const handleAddSurgery = () => {
    setNewSurgery({ name: '', date: '', surgeon: '', facility: '' });
  };

  const handleSaveNewSurgery = async () => {
    if (!newSurgery?.name?.trim()) {
      toast.error('Please enter surgery name');
      return;
    }
    try {
      setSavingSurgery('new');
      await addSurgery(newSurgery);
      setNewSurgery(null);
      toast.success('Surgery added');
    } catch (error) {
      toast.error('Failed to add surgery');
    } finally {
      setSavingSurgery(null);
    }
  };

  const handleUpdateSurgery = async (id, field, value) => {
    try {
      setSavingSurgery(id);
      await updateSurgery(id, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingSurgery(null);
    }
  };

  const handleDeleteSurgery = async (id) => {
    try {
      setSavingSurgery(id);
      await deleteSurgery(id);
      toast.success('Surgery removed');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSavingSurgery(null);
    }
  };

  // --- DOCTOR HANDLERS ---
  const handleAddDoctor = () => {
    setNewDoctor({ name: '', specialty: '', phone: '', clinic: '', is_primary: false });
  };

  const handleSaveNewDoctor = async () => {
    if (!newDoctor?.name?.trim()) {
      toast.error('Please enter doctor name');
      return;
    }
    try {
      setSavingDoctor('new');
      await addDoctor(newDoctor);
      setNewDoctor(null);
      toast.success('Doctor added');
    } catch (error) {
      toast.error('Failed to add doctor');
    } finally {
      setSavingDoctor(null);
    }
  };

  const handleUpdateDoctor = async (id, field, value) => {
    try {
      setSavingDoctor(id);
      await updateDoctor(id, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingDoctor(null);
    }
  };

  const handleDeleteDoctor = async (id) => {
    try {
      setSavingDoctor(id);
      await deleteDoctor(id);
      toast.success('Doctor removed');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSavingDoctor(null);
    }
  };

  // --- INSURANCE HANDLERS ---
  const handleAddInsurance = () => {
    setNewInsurance({ provider: '', policy_number: '', group_number: '', phone: '', is_primary: false });
  };

  const handleSaveNewInsurance = async () => {
    if (!newInsurance?.provider?.trim()) {
      toast.error('Please enter insurance provider');
      return;
    }
    try {
      setSavingInsurance('new');
      await addInsurance(newInsurance);
      setNewInsurance(null);
      toast.success('Insurance added');
    } catch (error) {
      toast.error('Failed to add insurance');
    } finally {
      setSavingInsurance(null);
    }
  };

  const handleUpdateInsurance = async (id, field, value) => {
    try {
      setSavingInsurance(id);
      await updateInsurance(id, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingInsurance(null);
    }
  };

  const handleDeleteInsurance = async (id) => {
    try {
      setSavingInsurance(id);
      await deleteInsurance(id);
      toast.success('Insurance removed');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSavingInsurance(null);
    }
  };

  // Handler for scanned insurance card data
  const handleInsuranceCardScanned = (extractedData) => {
    // Pre-fill the new insurance form with extracted data
    setNewInsurance({
      provider: extractedData.provider || '',
      policy_number: extractedData.policy_number || '',
      group_number: extractedData.group_number || '',
      phone: extractedData.phone || '',
      is_primary: insurance.length === 0, // Make primary if first insurance
    });
    setShowInsuranceScanner(false);
    toast.success('Card data extracted! Please review and save.');
  };

  // --- EMERGENCY CONTACT HANDLERS ---
  const handleAddContact = () => {
    setNewContact({ name: '', relationship: '', phone: '', is_primary: false, contact_type: 'emergency' });
  };

  const handleSaveNewContact = async () => {
    if (!newContact?.name?.trim()) {
      toast.error('Please enter contact name');
      return;
    }
    try {
      setSavingContact('new');
      await addEmergencyContact(newContact);
      setNewContact(null);
      toast.success('Contact added');
    } catch (error) {
      toast.error('Failed to add contact');
    } finally {
      setSavingContact(null);
    }
  };

  const handleUpdateContact = async (id, field, value) => {
    try {
      setSavingContact(id);
      await updateEmergencyContact(id, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingContact(null);
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      setSavingContact(id);
      await deleteEmergencyContact(id);
      toast.success('Contact removed');
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setSavingContact(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="medical-info-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/emergency-binder`)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">Medical Information</h1>
              <p className="text-xs text-slate-500">{profileName}</p>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded-full">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs text-emerald-700 font-medium">Auto-saved</span>
            </div>
          </div>
        </div>
      </header>

      {/* Sync Status Banner - shows when there are pending changes */}
      {hasPendingChanges && (
        <div className="max-w-2xl mx-auto px-4 pt-4">
          <SyncStatusIndicator variant="full" size="md" />
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* Personal Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-5 w-5 text-red-500" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Full Name - full width on mobile */}
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={profileForm.full_name}
                onChange={(e) => setProfileForm(prev => ({ ...prev, full_name: e.target.value }))}
                onBlur={handleSaveProfile}
                placeholder="Full legal name"
                data-testid="full-name-input"
              />
            </div>
            {/* Date of Birth - can be narrow */}
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={profileForm.date_of_birth}
                onChange={(e) => setProfileForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                onBlur={handleSaveProfile}
                data-testid="dob-input"
              />
            </div>
            {/* Blood Type and Organ Donor - naturally short, keep side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Blood Type</Label>
                <Select
                  value={profileForm.blood_type}
                  onValueChange={(value) => {
                    setProfileForm(prev => ({ ...prev, blood_type: value }));
                    updateProfile({ blood_type: value });
                  }}
                >
                  <SelectTrigger data-testid="blood-type-select">
                    <SelectValue placeholder="Select blood type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOOD_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Organ Donor</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={profileForm.organ_donor}
                    onCheckedChange={(checked) => {
                      setProfileForm(prev => ({ ...prev, organ_donor: checked }));
                      updateProfile({ organ_donor: checked });
                    }}
                    data-testid="organ-donor-switch"
                  />
                  <span className="text-sm text-slate-600">
                    {profileForm.organ_donor ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Allergies */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Allergies
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddAllergy}
                disabled={newAllergy !== null}
                data-testid="add-allergy-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New allergy form */}
            {newAllergy && (
              <div className="p-4 bg-amber-50 rounded-lg space-y-3 border border-amber-200">
                {/* Allergy name - full width, severity below on mobile */}
                <div className="space-y-3">
                  <Input
                    placeholder="Allergy name (e.g., Penicillin)"
                    value={newAllergy.name}
                    onChange={(e) => setNewAllergy(prev => ({ ...prev, name: e.target.value }))}
                    autoFocus
                  />
                  <Select
                    value={newAllergy.severity}
                    onValueChange={(value) => setNewAllergy(prev => ({ ...prev, severity: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Severity level" />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITY_OPTIONS.map(s => (
                        <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  placeholder="Reaction symptoms"
                  value={newAllergy.reaction || ''}
                  onChange={(e) => setNewAllergy(prev => ({ ...prev, reaction: e.target.value }))}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNewAllergy}
                    disabled={savingAllergy === 'new'}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {savingAllergy === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setNewAllergy(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing allergies */}
            {allergies.length === 0 && !newAllergy ? (
              <EmptyState 
                type="allergies"
                onAction={handleAddAllergy}
                actionLabel="Add First Allergy"
                showExamples={true}
              />
            ) : (
              allergies.map((allergy) => (
                <div key={allergy.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Allergy name - full width */}
                      <Input
                        placeholder="Allergy name"
                        value={allergy.name || ''}
                        onChange={(e) => handleUpdateAllergy(allergy.id, 'name', e.target.value)}
                      />
                      {/* Severity - full width */}
                      <Select
                        value={allergy.severity || ''}
                        onValueChange={(value) => handleUpdateAllergy(allergy.id, 'severity', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Severity level" />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITY_OPTIONS.map(s => (
                            <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => handleDeleteAllergy(allergy.id)}
                      disabled={savingAllergy === allergy.id}
                    >
                      {savingAllergy === allergy.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Input
                    placeholder="Reaction symptoms"
                    value={allergy.reaction || ''}
                    onChange={(e) => handleUpdateAllergy(allergy.id, 'reaction', e.target.value)}
                  />
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Medications */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Pill className="h-5 w-5 text-blue-500" />
                Medications
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddMedication}
                disabled={newMedication !== null}
                data-testid="add-medication-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New medication form */}
            {newMedication && (
              <div className="p-4 bg-blue-50 rounded-lg space-y-3 border border-blue-200">
                {/* Medication name - full width */}
                <Input
                  placeholder="Medication name"
                  value={newMedication.name}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
                {/* Dosage and Frequency - can be paired */}
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Dosage"
                    value={newMedication.dosage || ''}
                    onChange={(e) => setNewMedication(prev => ({ ...prev, dosage: e.target.value }))}
                  />
                  <Input
                    placeholder="Frequency"
                    value={newMedication.frequency || ''}
                    onChange={(e) => setNewMedication(prev => ({ ...prev, frequency: e.target.value }))}
                  />
                </div>
                {/* Doctor - full width */}
                <Input
                  placeholder="Prescribing doctor"
                  value={newMedication.prescribing_doctor || ''}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, prescribing_doctor: e.target.value }))}
                />
                {/* Reason - full width */}
                <Input
                  placeholder="Prescribed for (reason)"
                  value={newMedication.prescribed_for || ''}
                  onChange={(e) => setNewMedication(prev => ({ ...prev, prescribed_for: e.target.value }))}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNewMedication}
                    disabled={savingMedication === 'new'}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {savingMedication === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setNewMedication(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing medications */}
            {medications.length === 0 && !newMedication ? (
              <EmptyState 
                type="medications"
                onAction={handleAddMedication}
                actionLabel="Add First Medication"
                showExamples={true}
              />
            ) : (
              medications.map((med) => (
                <div key={med.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Medication name - full width */}
                      <Input
                        placeholder="Medication name"
                        value={med.name || ''}
                        onChange={(e) => handleUpdateMedication(med.id, 'name', e.target.value)}
                      />
                      {/* Dosage and Frequency - paired short values */}
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Dosage"
                          value={med.dosage || ''}
                          onChange={(e) => handleUpdateMedication(med.id, 'dosage', e.target.value)}
                        />
                        <Input
                          placeholder="Frequency"
                          value={med.frequency || ''}
                          onChange={(e) => handleUpdateMedication(med.id, 'frequency', e.target.value)}
                        />
                      </div>
                      {/* Doctor - full width */}
                      <Input
                        placeholder="Prescribing doctor"
                        value={med.prescribing_doctor || ''}
                        onChange={(e) => handleUpdateMedication(med.id, 'prescribing_doctor', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => handleDeleteMedication(med.id)}
                      disabled={savingMedication === med.id}
                    >
                      {savingMedication === med.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={med.is_active !== false}
                        onCheckedChange={(checked) => handleUpdateMedication(med.id, 'is_active', checked)}
                      />
                      <span className="text-sm text-slate-600">
                        {med.is_active !== false ? 'Active' : 'Discontinued'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Medical Conditions */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-500" />
                Medical Conditions
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddCondition}
                disabled={newCondition !== null}
                data-testid="add-condition-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New condition form */}
            {newCondition && (
              <div className="p-4 bg-purple-50 rounded-lg space-y-3 border border-purple-200">
                {/* Condition name - full width */}
                <Input
                  placeholder="Condition name"
                  value={newCondition.condition}
                  onChange={(e) => setNewCondition(prev => ({ ...prev, condition: e.target.value }))}
                  autoFocus
                />
                {/* Date - full width */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Diagnosed Date</Label>
                  <Input
                    type="date"
                    value={newCondition.diagnosed_date || ''}
                    onChange={(e) => setNewCondition(prev => ({ ...prev, diagnosed_date: e.target.value }))}
                  />
                </div>
                {/* Doctor - full width */}
                <Input
                  placeholder="Diagnosing doctor"
                  value={newCondition.diagnosing_doctor || ''}
                  onChange={(e) => setNewCondition(prev => ({ ...prev, diagnosing_doctor: e.target.value }))}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNewCondition}
                    disabled={savingCondition === 'new'}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {savingCondition === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setNewCondition(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing conditions */}
            {conditions.length === 0 && !newCondition ? (
              <EmptyState 
                type="conditions"
                onAction={handleAddCondition}
                actionLabel="Add First Condition"
                showExamples={true}
              />
            ) : (
              conditions.map((condition) => (
                <div key={condition.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Condition name - full width */}
                      <Input
                        placeholder="Condition name"
                        value={condition.condition || ''}
                        onChange={(e) => handleUpdateCondition(condition.id, 'condition', e.target.value)}
                      />
                      {/* Date - full width */}
                      <Input
                        type="date"
                        value={condition.diagnosed_date || ''}
                        onChange={(e) => handleUpdateCondition(condition.id, 'diagnosed_date', e.target.value)}
                      />
                      {/* Doctor - full width */}
                      <Input
                        placeholder="Diagnosing doctor"
                        value={condition.diagnosing_doctor || ''}
                        onChange={(e) => handleUpdateCondition(condition.id, 'diagnosing_doctor', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => handleDeleteCondition(condition.id)}
                      disabled={savingCondition === condition.id}
                    >
                      {savingCondition === condition.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Surgeries */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Scissors className="h-5 w-5 text-rose-500" />
                Surgeries & Procedures
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddSurgery}
                disabled={newSurgery !== null}
                data-testid="add-surgery-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New surgery form */}
            {newSurgery && (
              <div className="p-4 bg-rose-50 rounded-lg space-y-3 border border-rose-200">
                {/* Surgery name - full width */}
                <Input
                  placeholder="Surgery/procedure name"
                  value={newSurgery.name}
                  onChange={(e) => setNewSurgery(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
                {/* Date - full width */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Date</Label>
                  <Input
                    type="date"
                    value={newSurgery.date || ''}
                    onChange={(e) => setNewSurgery(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
                {/* Surgeon - full width */}
                <Input
                  placeholder="Surgeon name"
                  value={newSurgery.surgeon || ''}
                  onChange={(e) => setNewSurgery(prev => ({ ...prev, surgeon: e.target.value }))}
                />
                {/* Facility - full width */}
                <Input
                  placeholder="Hospital/Facility"
                  value={newSurgery.facility || ''}
                  onChange={(e) => setNewSurgery(prev => ({ ...prev, facility: e.target.value }))}
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNewSurgery}
                    disabled={savingSurgery === 'new'}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    {savingSurgery === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setNewSurgery(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing surgeries */}
            {surgeries.length === 0 && !newSurgery ? (
              <EmptyState 
                type="surgeries"
                onAction={handleAddSurgery}
                actionLabel="Add First Surgery"
                showExamples={true}
              />
            ) : (
              surgeries.map((surgery) => (
                <div key={surgery.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Surgery name - full width */}
                      <Input
                        placeholder="Surgery name"
                        value={surgery.name || ''}
                        onChange={(e) => handleUpdateSurgery(surgery.id, 'name', e.target.value)}
                      />
                      {/* Date - full width */}
                      <Input
                        type="date"
                        value={surgery.date || ''}
                        onChange={(e) => handleUpdateSurgery(surgery.id, 'date', e.target.value)}
                      />
                      {/* Surgeon - full width */}
                      <Input
                        placeholder="Surgeon"
                        value={surgery.surgeon || ''}
                        onChange={(e) => handleUpdateSurgery(surgery.id, 'surgeon', e.target.value)}
                      />
                      {/* Facility - full width */}
                      <Input
                        placeholder="Hospital/Facility"
                        value={surgery.facility || ''}
                        onChange={(e) => handleUpdateSurgery(surgery.id, 'facility', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => handleDeleteSurgery(surgery.id)}
                      disabled={savingSurgery === surgery.id}
                    >
                      {savingSurgery === surgery.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Doctors */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-emerald-500" />
                Doctors
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddDoctor}
                disabled={newDoctor !== null}
                data-testid="add-doctor-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New doctor form */}
            {newDoctor && (
              <div className="p-4 bg-emerald-50 rounded-lg space-y-3 border border-emerald-200">
                {/* Doctor name - full width */}
                <Input
                  placeholder="Doctor name"
                  value={newDoctor.name}
                  onChange={(e) => setNewDoctor(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
                {/* Specialty - full width */}
                <Input
                  placeholder="Specialty"
                  value={newDoctor.specialty || ''}
                  onChange={(e) => setNewDoctor(prev => ({ ...prev, specialty: e.target.value }))}
                />
                {/* Phone - full width */}
                <Input
                  placeholder="Phone number"
                  value={newDoctor.phone || ''}
                  onChange={(e) => setNewDoctor(prev => ({ ...prev, phone: e.target.value }))}
                />
                {/* Clinic - full width */}
                <Input
                  placeholder="Clinic/Practice name"
                  value={newDoctor.clinic || ''}
                  onChange={(e) => setNewDoctor(prev => ({ ...prev, clinic: e.target.value }))}
                />
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newDoctor.is_primary}
                    onCheckedChange={(checked) => setNewDoctor(prev => ({ ...prev, is_primary: checked }))}
                  />
                  <span className="text-sm text-slate-600">Primary doctor</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNewDoctor}
                    disabled={savingDoctor === 'new'}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {savingDoctor === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setNewDoctor(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing doctors */}
            {doctors.length === 0 && !newDoctor ? (
              <EmptyState 
                type="doctors"
                onAction={handleAddDoctor}
                actionLabel="Add First Doctor"
                showExamples={true}
              />
            ) : (
              doctors.map((doctor) => (
                <div key={doctor.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Doctor name - full width with primary indicator */}
                      <div className="relative">
                        <Input
                          placeholder="Doctor name"
                          value={doctor.name || ''}
                          onChange={(e) => handleUpdateDoctor(doctor.id, 'name', e.target.value)}
                          className={doctor.is_primary ? 'pr-8' : ''}
                        />
                        {doctor.is_primary && (
                          <Star className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500 fill-amber-500" />
                        )}
                      </div>
                      {/* Specialty - full width */}
                      <Input
                        placeholder="Specialty"
                        value={doctor.specialty || ''}
                        onChange={(e) => handleUpdateDoctor(doctor.id, 'specialty', e.target.value)}
                      />
                      {/* Phone - full width */}
                      <Input
                        placeholder="Phone number"
                        value={doctor.phone || ''}
                        onChange={(e) => handleUpdateDoctor(doctor.id, 'phone', e.target.value)}
                      />
                      {/* Clinic - full width */}
                      <Input
                        placeholder="Clinic/Practice name"
                        value={doctor.clinic || ''}
                        onChange={(e) => handleUpdateDoctor(doctor.id, 'clinic', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => handleDeleteDoctor(doctor.id)}
                      disabled={savingDoctor === doctor.id}
                    >
                      {savingDoctor === doctor.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={doctor.is_primary}
                      onCheckedChange={(checked) => handleUpdateDoctor(doctor.id, 'is_primary', checked)}
                    />
                    <span className="text-sm text-slate-600">Primary doctor</span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Insurance */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-indigo-500" />
                Insurance
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowInsuranceScanner(true)}
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                  data-testid="scan-insurance-btn"
                >
                  <Camera className="h-4 w-4 mr-1" />
                  Scan Card
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddInsurance}
                  disabled={newInsurance !== null}
                  data-testid="add-insurance-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New insurance form */}
            {newInsurance && (
              <div className="p-4 bg-indigo-50 rounded-lg space-y-3 border border-indigo-200">
                <Input
                  placeholder="Insurance provider *"
                  value={newInsurance.provider}
                  onChange={(e) => setNewInsurance(prev => ({ ...prev, provider: e.target.value }))}
                  autoFocus
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Policy number"
                    value={newInsurance.policy_number || ''}
                    onChange={(e) => setNewInsurance(prev => ({ ...prev, policy_number: e.target.value }))}
                  />
                  <Input
                    placeholder="Group number"
                    value={newInsurance.group_number || ''}
                    onChange={(e) => setNewInsurance(prev => ({ ...prev, group_number: e.target.value }))}
                  />
                </div>
                <Input
                  placeholder="Phone number"
                  value={newInsurance.phone || ''}
                  onChange={(e) => setNewInsurance(prev => ({ ...prev, phone: e.target.value }))}
                />
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newInsurance.is_primary}
                    onCheckedChange={(checked) => setNewInsurance(prev => ({ ...prev, is_primary: checked }))}
                  />
                  <span className="text-sm text-slate-600">Primary insurance</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNewInsurance}
                    disabled={savingInsurance === 'new'}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {savingInsurance === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setNewInsurance(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing insurance */}
            {insurance.length === 0 && !newInsurance ? (
              <p className="text-sm text-slate-500 text-center py-4">No insurance added</p>
            ) : (
              insurance.map((ins) => (
                <div key={ins.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Provider - full width with primary indicator */}
                      <div className="relative">
                        <Input
                          placeholder="Insurance provider"
                          value={ins.provider || ''}
                          onChange={(e) => handleUpdateInsurance(ins.id, 'provider', e.target.value)}
                          className={ins.is_primary ? 'pr-8' : ''}
                        />
                        {ins.is_primary && (
                          <Star className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500 fill-amber-500" />
                        )}
                      </div>
                      {/* Policy and Group numbers - paired short values */}
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Policy #"
                          value={ins.policy_number || ''}
                          onChange={(e) => handleUpdateInsurance(ins.id, 'policy_number', e.target.value)}
                        />
                        <Input
                          placeholder="Group #"
                          value={ins.group_number || ''}
                          onChange={(e) => handleUpdateInsurance(ins.id, 'group_number', e.target.value)}
                        />
                      </div>
                      {/* Phone - full width */}
                      <Input
                        placeholder="Phone number"
                        value={ins.phone || ''}
                        onChange={(e) => handleUpdateInsurance(ins.id, 'phone', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => handleDeleteInsurance(ins.id)}
                      disabled={savingInsurance === ins.id}
                    >
                      {savingInsurance === ins.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={ins.is_primary}
                      onCheckedChange={(checked) => handleUpdateInsurance(ins.id, 'is_primary', checked)}
                    />
                    <span className="text-sm text-slate-600">Primary insurance</span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Emergency Contacts */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Phone className="h-5 w-5 text-teal-500" />
                Emergency Contacts
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddContact}
                disabled={newContact !== null}
                data-testid="add-contact-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* New contact form */}
            {newContact && (
              <div className="p-4 bg-teal-50 rounded-lg space-y-3 border border-teal-200">
                {/* Contact name - full width */}
                <Input
                  placeholder="Contact name *"
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
                {/* Relationship - full width */}
                <Input
                  placeholder="Relationship"
                  value={newContact.relationship || ''}
                  onChange={(e) => setNewContact(prev => ({ ...prev, relationship: e.target.value }))}
                />
                {/* Phone - full width */}
                <Input
                  placeholder="Phone number"
                  value={newContact.phone || ''}
                  onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                />
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newContact.is_primary}
                    onCheckedChange={(checked) => setNewContact(prev => ({ ...prev, is_primary: checked }))}
                  />
                  <span className="text-sm text-slate-600">Primary contact</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSaveNewContact}
                    disabled={savingContact === 'new'}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {savingContact === 'new' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setNewContact(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Existing contacts */}
            {emergencyContacts.length === 0 && !newContact ? (
              <EmptyState 
                type="emergencyContacts"
                onAction={handleAddContact}
                actionLabel="Add First Contact"
                showExamples={true}
              />
            ) : (
              emergencyContacts.map((contact) => (
                <div key={contact.id} className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 space-y-3">
                      {/* Contact name - full width with primary indicator */}
                      <div className="relative">
                        <Input
                          placeholder="Contact name"
                          value={contact.name || ''}
                          onChange={(e) => handleUpdateContact(contact.id, 'name', e.target.value)}
                          className={contact.is_primary ? 'pr-8' : ''}
                        />
                        {contact.is_primary && (
                          <Star className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500 fill-amber-500" />
                        )}
                      </div>
                      {/* Relationship - full width */}
                      <Input
                        placeholder="Relationship"
                        value={contact.relationship || ''}
                        onChange={(e) => handleUpdateContact(contact.id, 'relationship', e.target.value)}
                      />
                      {/* Phone - full width */}
                      <Input
                        placeholder="Phone number"
                        value={contact.phone || ''}
                        onChange={(e) => handleUpdateContact(contact.id, 'phone', e.target.value)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 flex-shrink-0"
                      onClick={() => handleDeleteContact(contact.id)}
                      disabled={savingContact === contact.id}
                    >
                      {savingContact === contact.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Input
                    placeholder="Phone number"
                    value={contact.phone || ''}
                    onChange={(e) => handleUpdateContact(contact.id, 'phone', e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={contact.is_primary}
                      onCheckedChange={(checked) => handleUpdateContact(contact.id, 'is_primary', checked)}
                    />
                    <span className="text-sm text-slate-600">Primary contact</span>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Info banner */}
        <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">Changes saved automatically</p>
            <p className="text-xs text-blue-600 mt-1">
              All medical information is synced across your Emergency Summary, QR code, Doctor Summary, and PDF exports.
            </p>
          </div>
        </div>

      </main>

      {/* Insurance Card Scanner Modal */}
      {showInsuranceScanner && (
        <InsuranceCardScanner
          onExtracted={handleInsuranceCardScanned}
          onClose={() => setShowInsuranceScanner(false)}
        />
      )}
    </div>
  );
};

export default MedicalInfoPage;
